import { useTranslation } from 'react-i18next';
// Stats Card Component for Dashboard
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  useTheme,
  alpha
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  MoreVert
} from '@mui/icons-material';

const StatsCard = ({
  title,
  value,
  icon,
  color = 'primary',
  trend,
  trendValue,
  subtitle,
  onClick,
  loading = false,
  ...props
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const getColorValue = (colorKey) => {
    if (typeof color === 'string' && theme.palette[color]) {
      return theme.palette[color].main;
    }
    return color;
  };

  const colorValue = getColorValue(color);

  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'down':
        return <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />;
      case t('hardcoded.components_dashboard_statscard.neutral'):
      default:
        return <TrendingFlat sx={{ fontSize: 16, color: 'text.secondary' }} />;
    }
  };

  const handleCardClick = () => {
    if (onClick && !loading) {
      onClick();
    }
  };

  return (
    <Card
      {...props}
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          ...(onClick && {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8],
            borderColor: colorValue,
          }),
        },
        ...props.sx,
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: 2,
              backgroundColor: alpha(colorValue, 0.1),
              color: colorValue,
            }}
          >
            {icon}
          </Box>
          <IconButton
            size="small"
            sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
            onClick={(e) => {
              e.stopPropagation();
              // Handle menu click if needed
            }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>

        {/* Main Content */}
        <Box>
          <Typography
            variant="h4"
            component="div"
            fontWeight={700}
            color="text.primary"
            sx={{
              mb: 0.5,
              lineHeight: 1.2,
            }}
          >
            {loading ? '...' : typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={500}
            sx={{ mb: 1 }}
          >
            {title}
          </Typography>

          {/* Subtitle */}
          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              {subtitle}
            </Typography>
          )}

          {/* Trend */}
          {(trend || trendValue) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {trend && getTrendIcon()}
              {trendValue && (
                <Typography
                  variant="caption"
                  sx={{
                    color: trend === 'up' ? 'success.main' : trend === 'down' ? 'error.main' : 'text.secondary',
                    fontWeight: 500,
                  }}
                >
                  {trendValue}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default StatsCard;